import { useState, useCallback } from 'react';
import { observer } from 'mobx-react-lite';
import { bibleState } from '@/state/bible-state';
import { formatReference } from '@skjv/common';
import type { Bookmark } from '@skjv/common';
import { BookmarkIcon, StarIcon, TrashIcon } from '@components/icons';
import { EmptyState } from '@components/common';

/**
 * Bookmarks tab content
 */
export const BookmarksContent = observer(function BookmarksContent() {
  const [searchQuery, setSearchQuery] = useState('');
  const bookmarks = bibleState.sortedBookmarks;

  const filteredBookmarks = searchQuery
    ? bookmarks.filter(
        (bm) =>
          bm.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
          formatReference(bm).toLowerCase().includes(searchQuery.toLowerCase()),
      )
    : bookmarks;

  const handleLoad = useCallback((bookmark: Bookmark) => {
    console.log('[Bookmarks] Loading bookmark:', bookmark);
    bibleState.setCurrentLocation(bookmark);
    // Keep panel open so user can load multiple bookmarks
  }, []);

  const handleDelete = useCallback((name: string) => {
    bibleState.showConfirmDialog({
      title: 'Delete Bookmark',
      message: 'Delete this bookmark?',
      confirmText: 'Delete',
      variant: 'danger',
      onConfirm: () => bibleState.removeBookmark(name),
    });
  }, []);

  const handleToggleFavorite = useCallback((name: string, currentFavorite: boolean | undefined) => {
    bibleState.setFavoriteBookmark(name, !currentFavorite);
  }, []);

  return (
    <>
      {/* Search (only show if > 12 bookmarks) */}
      {bookmarks.length > 12 && (
        <div className="mb-3">
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search bookmarks..."
            className="bookmark-input"
          />
        </div>
      )}

      {/* Bookmark list */}
      {filteredBookmarks.length === 0 ? (
        bookmarks.length === 0 ? (
          <EmptyState
            icon={<BookmarkIcon className="h-10 w-10" />}
            title="No bookmarks yet"
            hint="Tap a verse number and choose Set Bookmark to save your place"
          />
        ) : (
          <div className="py-8 text-center text-stone-500 dark:text-stone-400">
            No matching bookmarks
          </div>
        )
      ) : (
        filteredBookmarks.map((bookmark) => (
          <div key={bookmark.name} className="bookmark-row">
            <div className="bookmark-row-header">
              <div>
                <div className="bookmark-name">{bookmark.name}</div>
                <div className="bookmark-reference">{formatReference(bookmark)}</div>
              </div>
            </div>
            <div className="bookmark-actions">
              <button
                onClick={() => handleToggleFavorite(bookmark.name, bookmark.isFavorite)}
                className={`bookmark-action-btn ${bookmark.isFavorite ? 'text-amber-500' : ''}`}
                title={bookmark.isFavorite ? 'Remove from favorites' : 'Add to favorites'}
              >
                <StarIcon className="h-5 w-5" filled={bookmark.isFavorite} />
              </button>
              <button
                onClick={() => handleDelete(bookmark.name)}
                className="bookmark-action-btn hover:text-red-600 dark:hover:text-red-400"
                title="Delete"
              >
                <TrashIcon className="h-5 w-5" />
              </button>
              <div className="flex-1" />
              <button onClick={() => handleLoad(bookmark)} className="bookmark-load-btn">
                Load
              </button>
            </div>
          </div>
        ))
      )}
    </>
  );
});
